import { queryClient } from "@lib/queryClient";
import newTokenService from "./newTokenService";
import mfaService from "./mfaService";

// Chaves de sessionStorage/localStorage que pertencem à PESSOA logada, não ao navegador.
// O hospital ativo mora em `h` (+ três espelhos); `User_id_senne` é o id da pessoa.
const ACTIVE_HOSPITAL_KEYS = ["h", "hospital", "hospitalName", "permissions"] as const;
const USER_ID_KEY = "User_id_senne";

/**
 * Remove o hospital ativo guardado (sessionStorage e as cópias legadas do localStorage).
 * Só toca storage — sem rede.
 */
export function clearActiveHospitalStorage(): void {
  for (const key of ACTIVE_HOSPITAL_KEYS) {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  }
}

/**
 * Fim DEFINITIVO de uma sessão, parte que vive em storage/memória de serviço: tokens, sessão
 * MFA, hospital ativo, id e usuário em cache. NÃO toca no cache de queries.
 *
 * É o que o cliente HTTP e o bootstrap chamam: ali nada navega, e `queryClient.clear()` com
 * observers montados faz toda query refazer o fetch no próximo render (sem Authorization →
 * 401 → mais refresh recusado; páginas `useSuspenseQuery` caem em "Carregando página..." e
 * um erro de suspense pode chegar ao `PageErrorBoundary`). No bootstrap o cache ainda está
 * vazio (é memória, a página acabou de carregar), então não há o que limpar.
 *
 * NÃO chamar em refresh recuperável: no Portal 2.0 a sessão só termina em "Sair" ou quando o
 * servidor a recusa — reload, aba nova e token que o refresh renova não encerram nada.
 * Não navega e não encerra o auto-refresh; quem chama faz isso.
 *
 * Dependência: `mfaService` importa `newApi/api`, que importa este módulo (ciclo
 * api → sessionState → mfaService → api). É inofensivo: só se usa `mfaService` dentro de função,
 * nunca na avaliação do módulo.
 */
export function clearSessionStorage(): void {
  newTokenService.clearTokens();
  mfaService.clearMFASession();
  clearActiveHospitalStorage();
  sessionStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem("new_auth:user");
  localStorage.removeItem("auth:user");
}

/**
 * `clearSessionStorage()` + cache de queries (as chaves levam o id do hospital; entrar com
 * outra conta mostraria dado da anterior). SÓ onde uma navegação para /login acontece em
 * seguida e desmonta os observers: hoje, `logout()`.
 */
export function clearSessionState(): void {
  clearSessionStorage();
  queryClient.clear();
}

/**
 * Antes de guardar uma sessão NOVA (login direto): o que sobrou da anterior neste navegador não
 * é desta pessoa. Os fins de sessão do `api.ts` não navegam por `logout()`, então o cache de
 * queries (gcTime 30 min, memória) sobrevive a eles — e há chaves sem identidade
 * (`['carlao-notifications', 1, 20]`, `['auth','me']`) que mostrariam o dado da pessoa anterior
 * antes do refetch. Em /login nada observa essas queries, então limpar aqui não gera rajada de
 * refetch. Ordem: chamar ANTES de guardar tokens e usuário novos.
 */
export function clearPreviousIdentityState(): void {
  clearActiveHospitalStorage();
  queryClient.clear();
}
